// @flow
import type { StoreState } from 'reducers';

import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import i18n from 'i18n-js';
import { Icon, Popup } from 'semantic-grab-ui/src';

import useCurrencyConfigs from './useCurrencyConfigs';
import { FLOW_EARN, getFiatMajorPerUno, getGrpPerUnoMajor, selectConfigForCountry } from './currencyConfig';

import './CurrencyRateTooltip.scss';

type Props = {
  flowType: string,
  country: ?string,
  appState: Object,
  // See CurrencyAmountPreview's prop of the same name.
  onLoadError?: (hasError: boolean) => void,
};

export function CurrencyRateTooltip(props: Props) {
  const { flowType, country, appState, onLoadError } = props;
  const { configs, error } = useCurrencyConfigs(appState);

  const lastReportedLoadError = useRef(null);
  useEffect(() => {
    if (!onLoadError) return;
    if (lastReportedLoadError.current === error) return;
    lastReportedLoadError.current = error;
    onLoadError(error);
  }, [onLoadError, error]);

  if (error) return null;

  const config = selectConfigForCountry(configs, country);
  if (!config) return null;

  const fiat = getFiatMajorPerUno(config);
  const isEarn = flowType === FLOW_EARN;
  const blockSize = isEarn ? config.earnBlockSizeInGRP : config.burnBlockSizeInGRP;

  const content = (
    <div className="currencyRateTooltip">
      <div className="currencyRateTooltip__title">
        {i18n.t('currency_preview.tooltip.title')}
      </div>
      <div className="currencyRateTooltip__line">
        {i18n.t('currency_preview.tooltip.previous_rate', {
          coins: getGrpPerUnoMajor(config),
          fiat,
          currency: config.fiatCurrency,
        })}
      </div>
      <div className="currencyRateTooltip__line">
        {i18n.t('currency_preview.tooltip.new_rate', {
          fiat,
          currency: config.fiatCurrency,
        })}
      </div>
      <div className="currencyRateTooltip__line">
        {i18n.t(
          isEarn
            ? 'currency_preview.tooltip.earn_block_size'
            : 'currency_preview.tooltip.burn_block_size',
          { blockSize }
        )}
      </div>
    </div>
  );

  return (
    <Popup
      trigger={<Icon name="info circle" className="currencyRateTooltip__trigger" />}
      content={content}
      className="currencyRateTooltip__popup"
      size="mini"
      wide
    />
  );
}

function mapStateToProps(state: StoreState) {
  return { appState: state.app };
}

export default connect(mapStateToProps)(CurrencyRateTooltip);
